'use client';

import { LiquidGlass } from '@/components/ui/liquid-glass/LiquidGlass';
import controls from './PublicSiteControls.module.css';

import Link from 'next/link';
import { ArrowRight, Pause, Play } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { SeoGuideArticle } from '@/lib/seo/guides';

const SLIDE_INTERVAL_MS = 4600;

const EDITORIAL_SLIDES = [
  {
    src: '/guides/editorial-fashion/01-black-coat.jpg',
    objectPosition: 'center 38%',
  },
  {
    src: '/guides/editorial-fashion/02-olive-jacket.jpg',
    objectPosition: 'center 42%',
  },
  {
    src: '/guides/editorial-fashion/03-beige-set.jpg',
    objectPosition: 'center 42%',
  },
  {
    src: '/guides/editorial-fashion/04-navy-trench.jpg',
    objectPosition: 'center 35%',
  },
  {
    src: '/guides/editorial-fashion/05-brown-utility.jpg',
    objectPosition: 'center 42%',
  },
  {
    src: '/guides/editorial-fashion/06-textured-jacket.jpg',
    objectPosition: 'center 42%',
  },
  {
    src: '/guides/editorial-fashion/07-belted-trench.jpg',
    objectPosition: 'center 38%',
  },
] as const;

interface GuideFeaturedSlideshowProps {
  guide: SeoGuideArticle;
  href: string;
  readGuideLabel: string;
  pauseLabel: string;
  resumeLabel: string;
}

export function GuideFeaturedSlideshow({ guide, href, readGuideLabel, pauseLabel, resumeLabel }: GuideFeaturedSlideshowProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => setAutoPlayEnabled(!mediaQuery.matches);
    updateMotionPreference();
    mediaQuery.addEventListener('change', updateMotionPreference);
    return () => mediaQuery.removeEventListener('change', updateMotionPreference);
  }, []);

  useEffect(() => {
    if (isPaused || userPaused || !autoPlayEnabled) return;
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % EDITORIAL_SLIDES.length);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [autoPlayEnabled, isPaused, userPaused]);

  return (
    <article
      className="group relative min-w-0 overflow-hidden rounded-[18px] border border-[#E3E7E8] bg-white shadow-[0_3px_12px_rgba(32,54,68,0.04)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_32px_96px_rgba(23,25,28,0.14)]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <Link
        href={href}
        className="block focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-[#4ABDE9]/30"
      >
        <div className="relative isolate aspect-[16/10] overflow-hidden bg-[#F2F3F5]">
          {EDITORIAL_SLIDES.map((slide, index) => (
            <img
              key={slide.src}
              src={slide.src}
              alt={guide.heroImageAlt}
              aria-hidden={index === activeIndex ? undefined : true}
              width={900}
              height={1350}
              className={`absolute inset-0 h-full w-full object-contain p-5 sm:p-8 ${index === activeIndex ? 'opacity-100' : 'pointer-events-none opacity-0'} transition-opacity duration-700 ease-out`}
              style={{ objectPosition: slide.objectPosition }}
            />
          ))}
        </div>
          <div className="px-5 pb-6 pt-5 text-[#203644] sm:px-8 sm:pb-8">
            <div className="flex items-end justify-between gap-4">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5B6A73]">{guide.primaryKeyword}</span>
              <span className="font-mono text-[11px] font-medium tracking-[0.16em] text-[#5B6A73]">
                {String(activeIndex + 1).padStart(2, '0')} / {String(EDITORIAL_SLIDES.length).padStart(2, '0')}
              </span>
            </div>
            <h2 className="mt-3 max-w-[560px] text-[24px] font-semibold leading-[1.12] tracking-[-0.035em] text-[#203644] sm:text-[28px]">
              {guide.title}
            </h2>
            <p className="mt-3 max-w-[590px] text-[14px] leading-7 text-[#5B6A73] sm:text-[15px]">
              {guide.description}
            </p>
            <span className="mt-5 inline-flex items-center gap-2 text-[14px] font-semibold text-[#203644]">
              {readGuideLabel}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </div>
      </Link>

      <div className="flex items-center justify-between gap-2 border-t border-[#D9E0E3] px-4 py-2 sm:px-6" role="group" aria-label={guide.title}>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-0.5">
          {EDITORIAL_SLIDES.map((slide, index) => (
            <button
              key={slide.src}
              type="button"
              aria-pressed={index === activeIndex}
              aria-label={`${index + 1} / ${EDITORIAL_SLIDES.length}`}
              onClick={() => setActiveIndex(index)}
              className="pointer-events-auto grid h-11 w-8 place-items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4ABDE9]"
            ><span aria-hidden className={`block h-1.5 rounded-full transition-all ${index === activeIndex ? 'w-6 bg-[#4ABDE9]' : 'w-1.5 bg-[#9AAAB3]'}`} /></button>
          ))}
        </div>
        <LiquidGlass as="button" type="button" aria-label={userPaused ? resumeLabel : pauseLabel} aria-pressed={userPaused} onClick={() => setUserPaused((paused) => !paused)} className={`${controls.button} ${controls.secondary} ${controls.icon} ml-auto pointer-events-auto`}>
          {userPaused ? <Play size={16} aria-hidden /> : <Pause size={16} aria-hidden />}
        </LiquidGlass>
      </div>
    </article>
  );
}
